// Tvar odpovědí auth části API (docs/SPEC.md 2.1, 2.11). Better Auth samotný běží pod /v1/auth/*.
import { z } from 'zod'
import { phoneInputSchema, type AiTone, type AiTrust, type OrgSummary } from './org'

export const PASSWORD_MIN_LENGTH = 8

/** Profil (SPEC 2.10): jméno a telefon jdou přes Better Auth `updateUser` (pole `phone` je additionalField). */
export const updateProfileSchema = z.object({
  name: z.string().trim().min(2, 'Zadej jméno').max(120),
  phone: phoneInputSchema.optional().or(z.literal('')),
})
export type UpdateProfileInput = z.infer<typeof updateProfileSchema>

export const passwordSchema = z
  .string()
  .min(PASSWORD_MIN_LENGTH, `Heslo musí mít aspoň ${PASSWORD_MIN_LENGTH} znaků`)
  .max(128)

/** Změna hesla v Profilu (Better Auth `changePassword`, ostatní zařízení zůstanou přihlášená). */
export const changePasswordSchema = z.object({
  currentPassword: z.string().min(1, 'Zadej současné heslo'),
  newPassword: passwordSchema,
})
export type ChangePasswordInput = z.infer<typeof changePasswordSchema>

export const PLATFORM_ROLE_ADMIN = 'wac_admin'

export interface MeUser {
  id: string
  name: string
  email: string
  emailVerified: boolean
  image: string | null
  phone: string | null
  /** Platformní role: 'wac_admin' nebo 'user'. */
  role: string
}

export interface MeOrg extends OrgSummary {
  timezone: string
  locale: string
  city: string | null
  phoneE164: string | null
}

/** GET /v1/me */
export interface MeResponse {
  user: MeUser
  /** Aktivní organizace ze session, null když uživatel ještě žádnou nemá. */
  org: MeOrg | null
  /** Všechny firmy uživatele pro přepínání v menu profilu. */
  orgs: OrgSummary[]
  /** Když wac_admin vstoupil do účtu: id admina. Jinak null. */
  impersonatedBy: string | null
  isAdmin: boolean
}

/** GET /v1/auth-config (bez session): které způsoby přihlášení jsou zapnuté. */
export interface AuthConfigResponse {
  emailPassword: boolean
  magicLink: boolean
  google: boolean
  apple: boolean
}

/** GET /v1/admin/orgs: řádek agenturního přehledu (SPEC 2.11, PLAN 5.2). */
export interface AdminOrgRow {
  id: string
  name: string
  slug: string
  trade: string
  city: string | null
  createdAt: string
  ownerEmail: string | null
  membersCount: number
  clients: number
  jobs: number
  /** Poslední aktivita: max z clients.last_activity_at, interactions.occurred_at, jobs.updated_at. */
  lastActivityAt: string | null
  /** Návrhy Buddyho čekající na schválení (ai_actions ve stavu proposed). */
  proposalsPending: number
  onboardingCompletedAt: string | null
  /** Stav předplatného, null když firma žádné nemá. */
  subscriptionStatus: string | null
  plan: string | null
  /** Tokeny AI (vstup + výstup) za posledních 30 dní. */
  aiTokens30d: number
  aiCostMinor30d: number
  /** Majitel požádal o výmaz firmy (SPEC 2.10 Data). */
  deleteRequestedAt: string | null
}

export const ADMIN_ORG_SORTS = ['activity', 'created', 'name'] as const
export type AdminOrgSort = (typeof ADMIN_ORG_SORTS)[number]

export interface AdminOrgsResponse {
  data: AdminOrgRow[]
}

export interface AdminOrgMember {
  userId: string
  name: string
  email: string
  role: string
  /** Poslední aktualizace session člena (bez impersonací), null když se nikdy nepřihlásil. */
  lastSeenAt: string | null
}

export interface AdminOrgLeadSource {
  name: string
  type: string
  leads30d: number
}

/** GET /v1/admin/orgs/:id: detail firmy pro agenturní panel (bez tokenů a hesel). */
export interface AdminOrgDetail extends AdminOrgRow {
  settings: {
    timezone: string
    locale: string
    isVatPayer: boolean
    aiTone: AiTone
    aiTrust: AiTrust
    deleteRequestedAt: string | null
  }
  members: AdminOrgMember[]
  invitationsPending: number
  counts: {
    clients: number
    jobs: number
    /** Zakázky, které nejsou hotové, fakturované ani ztracené. */
    jobsOpen: number
    promisesOpen: number
    /** Záznamy produktové analytiky (analytics_events) za 30 dní. */
    events30d: number
    interactions30d: number
    voiceNotes30d: number
    proposalsPending: number
    proposalsApproved30d: number
  }
  channels: {
    telegram: boolean
    /** Poskytovatelé připojených kalendářů (google, microsoft). */
    calendars: string[]
  }
  leadSources: AdminOrgLeadSource[]
  usage: {
    calls30d: number
    tokens30d: number
    costMinor30d: number
  }
  brandKitDone: boolean
}

/** GET /v1/admin/overview: čísla na úvodu agenturního panelu. */
export interface AdminOverview {
  orgs: number
  /** Firmy s interakcí nebo změnou zakázky za posledních 7 dní. */
  activeOrgs7d: number
  newOrgs30d: number
  proposalsPending: number
  aiCostMinor30d: number
  deleteRequests: number
  /** Poptávky přes zdroje (lead_sources) za 30 dní. */
  leads30d: number
}
